import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import GetAppIcon from '@material-ui/icons/GetApp';
import Alert from '@material-ui/lab/Alert';
import { useNavigate } from 'react-router-dom';
import commissionService from '../../services/commissionService';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  title: {
    flex: '1 1 100%',
    marginBottom: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1),
  },
  headerActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  searchField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: '300px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formControl: {
    minWidth: 120,
  },
  summaryCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryValue: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
  },
}));

const CommissionsListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [commissionToDelete, setCommissionToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [filters, setFilters] = useState({
    status: 'all',
    period: 'all',
    partner: 'all'
  });
  const [summary, setSummary] = useState({
    totalCommissions: 0,
    pendingAmount: 0,
    paidAmount: 0,
    thisMonthAmount: 0
  });

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      // Récupérer les commissions via le service
      const fetchedCommissions = await commissionService.getAllCommissions();
      
      // Si aucune commission, utiliser des données de démonstration
      if (fetchedCommissions.length === 0) {
        const demoCommissions = [
          { 
            id: 'COM001', 
            partnerId: 'P001',
            partnerName: 'Entreprise ABC',
            tripId: 'T001',
            tripReference: 'VOY-2025-001',
            bookingId: 'B001',
            bookingReference: 'RES-2025-001',
            amount: 45.50,
            currency: 'EUR',
            status: 'pending',
            description: 'Commission pour trajet Paris-Lyon',
            createdAt: '2025-03-15T10:30:00',
            updatedAt: '2025-03-15T10:30:00',
            paymentDate: null
          },
          { 
            id: 'COM002', 
            partnerId: 'P002',
            partnerName: 'Transport Express',
            tripId: 'T002',
            tripReference: 'VOY-2025-002',
            bookingId: 'B002',
            bookingReference: 'RES-2025-002',
            amount: 32.75,
            currency: 'EUR',
            status: 'paid',
            description: 'Commission pour trajet Marseille-Nice',
            createdAt: '2025-03-10T14:45:00',
            updatedAt: '2025-03-12T09:20:00',
            paymentDate: '2025-03-12T09:20:00'
          },
          { 
            id: 'COM003', 
            partnerId: 'P001',
            partnerName: 'Entreprise ABC',
            tripId: 'T003',
            tripReference: 'VOY-2025-003',
            bookingId: 'B003',
            bookingReference: 'RES-2025-003',
            amount: 28.90,
            currency: 'EUR',
            status: 'pending',
            description: 'Commission pour trajet Lyon-Grenoble',
            createdAt: '2025-04-02T11:15:00',
            updatedAt: '2025-04-02T11:15:00',
            paymentDate: null
          },
          { 
            id: 'COM004', 
            partnerId: 'P003',
            partnerName: 'Voyages Confort',
            tripId: 'T004',
            tripReference: 'VOY-2025-004',
            bookingId: 'B004',
            bookingReference: 'RES-2025-004',
            amount: 52.20,
            currency: 'EUR',
            status: 'paid',
            description: 'Commission pour trajet Paris-Bordeaux',
            createdAt: '2025-03-25T16:30:00',
            updatedAt: '2025-04-01T10:45:00',
            paymentDate: '2025-04-01T10:45:00'
          },
          { 
            id: 'COM005', 
            partnerId: 'P002',
            partnerName: 'Transport Express',
            tripId: 'T005',
            tripReference: 'VOY-2025-005',
            bookingId: 'B005',
            bookingReference: 'RES-2025-005',
            amount: 38.60,
            currency: 'EUR',
            status: 'cancelled',
            description: 'Commission pour trajet Lille-Paris (annulée)',
            createdAt: '2025-03-18T09:10:00',
            updatedAt: '2025-03-19T14:25:00',
            paymentDate: null
          },
        ];
        setCommissions(demoCommissions);
        calculateSummary(demoCommissions);
      } else {
        setCommissions(fetchedCommissions);
        calculateSummary(fetchedCommissions);
      }
    } catch (error) {
      console.error('Error fetching commissions:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des commissions',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (commissionsList) => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    
    let totalCommissions = 0;
    let pendingAmount = 0;
    let paidAmount = 0;
    let thisMonthAmount = 0;
    
    commissionsList.forEach(commission => {
      if (commission.status !== 'cancelled') {
        totalCommissions += commission.amount;
        
        if (commission.status === 'pending') {
          pendingAmount += commission.amount;
        } else if (commission.status === 'paid') {
          paidAmount += commission.amount;
        }
        
        const commissionDate = new Date(commission.createdAt);
        if (commissionDate.getMonth() === thisMonth && commissionDate.getFullYear() === thisYear) {
          thisMonthAmount += commission.amount;
        }
      }
    });
    
    setSummary({
      totalCommissions,
      pendingAmount,
      paidAmount,
      thisMonthAmount
    });
  };

  const handleCreateCommission = () => {
    navigate('/admin/commissions/create');
  };

  const handleViewCommission = (commissionId) => {
    navigate(`/admin/commissions/${commissionId}`);
  };

  const handleEditCommission = (commissionId) => {
    navigate(`/admin/commissions/${commissionId}/edit`);
  };

  const handleDeleteClick = (commission) => {
    setCommissionToDelete(commission);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!commissionToDelete) return;
    
    try {
      // Supprimer la commission via le service
      await commissionService.deleteCommission(commissionToDelete.id);
      
      // Mettre à jour l'état local
      const updatedCommissions = commissions.filter(commission => commission.id !== commissionToDelete.id);
      setCommissions(updatedCommissions);
      calculateSummary(updatedCommissions);
      
      setSnackbar({
        open: true,
        message: 'Commission supprimée avec succès',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting commission:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression de la commission',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
      setCommissionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setCommissionToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleExportCSV = () => {
    // Logique d'exportation CSV
    // Dans une implémentation réelle, cela générerait un fichier CSV
    setSnackbar({
      open: true,
      message: 'Export CSV en cours...',
      severity: 'info'
    });
    
    // Simulation d'un délai pour l'export
    setTimeout(() => {
      setSnackbar({
        open: true,
        message: 'Commissions exportées avec succès',
        severity: 'success'
      });
    }, 1500);
  };

  const filteredCommissions = commissions.filter(commission => {
    // Filtre de recherche
    const matchesSearch = 
      commission.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      commission.partnerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      commission.tripReference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      commission.bookingReference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      commission.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtre de statut
    const matchesStatus = filters.status === 'all' || commission.status === filters.status;
    
    // Filtre de période
    let matchesPeriod = true;
    if (filters.period !== 'all') {
      const commissionDate = new Date(commission.createdAt);
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();
      
      if (filters.period === 'this_month') {
        matchesPeriod = commissionDate.getMonth() === thisMonth && commissionDate.getFullYear() === thisYear;
      } else if (filters.period === 'last_month') {
        const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
        const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;
        matchesPeriod = commissionDate.getMonth() === lastMonth && commissionDate.getFullYear() === lastMonthYear;
      } else if (filters.period === 'this_year') {
        matchesPeriod = commissionDate.getFullYear() === thisYear;
      }
    }
    
    // Filtre de partenaire
    const matchesPartner = filters.partner === 'all' || commission.partnerId === filters.partner;
    
    return matchesSearch && matchesStatus && matchesPeriod && matchesPartner;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'primary';
      case 'paid':
        return 'default';
      case 'cancelled':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'En attente';
      case 'paid':
        return 'Payée';
      case 'cancelled':
        return 'Annulée';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: currency 
    }).format(amount);
  };

  // Obtenir la liste unique des partenaires pour le filtre
  const uniquePartners = [...new Set(commissions.map(commission => commission.partnerId))];
  const partnerOptions = uniquePartners.map(partnerId => {
    const partner = commissions.find(commission => commission.partnerId === partnerId);
    return {
      id: partnerId,
      name: partner ? partner.partnerName : partnerId
    };
  });

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.headerActions}>
          <Typography className={classes.title} variant="h4" component="h1">
            Commissions
          </Typography>
          <div>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={handleExportCSV}
              className={classes.button}
            >
              Exporter CSV
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateCommission}
              className={classes.button}
            >
              Nouvelle commission
            </Button>
          </div>
        </div>
        
        {/* Résumé des commissions */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.summaryCard} elevation={2}>
              <Typography variant="body2" className={classes.summaryLabel}>
                Total des commissions
              </Typography>
              <Typography variant="h6" className={classes.summaryValue}>
                {formatCurrency(summary.totalCommissions)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.summaryCard} elevation={2}>
              <Typography variant="body2" className={classes.summaryLabel}>
                En attente de paiement
              </Typography>
              <Typography variant="h6" className={classes.summaryValue}>
                {formatCurrency(summary.pendingAmount)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.summaryCard} elevation={2}>
              <Typography variant="body2" className={classes.summaryLabel}>
                Commissions payées
              </Typography>
              <Typography variant="h6" className={classes.summaryValue}>
                {formatCurrency(summary.paidAmount)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.summaryCard} elevation={2}>
              <Typography variant="body2" className={classes.summaryLabel}>
                Ce mois-ci
              </Typography>
              <Typography variant="h6" className={classes.summaryValue}>
                {formatCurrency(summary.thisMonthAmount)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        <div className={classes.filters}>
          <TextField
            className={classes.searchField}
            label="Rechercher"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel id="status-filter-label">Statut</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              label="Statut"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="paid">Payée</MenuItem>
              <MenuItem value="cancelled">Annulée</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel id="period-filter-label">Période</InputLabel>
            <Select
              labelId="period-filter-label"
              id="period-filter"
              name="period"
              value={filters.period}
              onChange={handleFilterChange}
              label="Période"
            >
              <MenuItem value="all">Toutes les périodes</MenuItem>
              <MenuItem value="this_month">Ce mois-ci</MenuItem>
              <MenuItem value="last_month">Mois dernier</MenuItem>
              <MenuItem value="this_year">Cette année</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel id="partner-filter-label">Partenaire</InputLabel>
            <Select
              labelId="partner-filter-label"
              id="partner-filter"
              name="partner"
              value={filters.partner}
              onChange={handleFilterChange}
              label="Partenaire"
            >
              <MenuItem value="all">Tous les partenaires</MenuItem>
              {partnerOptions.map(partner => (
                <MenuItem key={partner.id} value={partner.id}>{partner.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table} aria-label="commissions table">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Partenaire</TableCell>
                  <TableCell>Référence voyage</TableCell>
                  <TableCell>Référence réservation</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Montant</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCommissions.length > 0 ? (
                  filteredCommissions.map((commission) => (
                    <TableRow key={commission.id}>
                      <TableCell component="th" scope="row">
                        <strong>{commission.id}</strong>
                      </TableCell>
                      <TableCell>{commission.partnerName}</TableCell>
                      <TableCell>{commission.tripReference}</TableCell>
                      <TableCell>{commission.bookingReference}</TableCell>
                      <TableCell>{formatDate(commission.createdAt)}</TableCell>
                      <TableCell>{formatCurrency(commission.amount, commission.currency)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(commission.status)} 
                          color={getStatusColor(commission.status)} 
                          size="small"
                          className={classes.chip}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          aria-label="view" 
                          color="primary"
                          onClick={() => handleViewCommission(commission.id)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton 
                          aria-label="edit" 
                          color="primary"
                          onClick={() => handleEditCommission(commission.id)}
                          disabled={commission.status === 'paid'}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          aria-label="delete" 
                          color="secondary"
                          onClick={() => handleDeleteClick(commission)}
                          disabled={commission.status === 'paid'}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Aucune commission trouvée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la suppression"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir supprimer définitivement la commission <strong>{commissionToDelete?.id}</strong> ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Annuler
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionsListPage;
